/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { BoxValues, type BoxValueEntry } from '@/lib/overview/boxValues';
import { ReverseDeviceSensor } from '@/lib/sensors/catalog/reverseDeviceSensor';
import type { TimeSeriesAggregation, TimeSeriesCalc } from '@/lib/timeSeries/timeSeriesValue';

export type MaybeArray<T> = T | T[];
export type MaybeNestedArray<T> = T | T[] | T[][];

export interface Api2ValueDataItem {
    aggregation: BoxValues<TimeSeriesAggregation>;
    date: Api2ValueDataValue<string | null>;
    errorMessage: Api2ValueDataValue<string>;
    formattedValue: BoxValues<string | null>;
    fractionDigits: BoxValues<number>;
    key: string;
    rawValue: BoxValues<number | null>;
    title: string | null;
    unit: BoxValues<string | null>;
    urn: BoxValues<string>;
    value: number | null | (number | null)[];
}

export interface Api2ValueDataResolvedConfig {
    aggregation: TimeSeriesAggregation;
    conversion: number;
    fractionDigits: number;
    groupIndex: number;
    key: string;
    offset: number;
    series?: string;
    title: string | null;
    unit: string | null;
    urn: string;
}

export interface Api2ValueDataRenderState {
    isLoading: boolean;
    items: Api2ValueDataItem[];
    loadingLabel: string;
}

export interface Api2ValueDataFlatItem {
    aggregation: TimeSeriesAggregation;
    date: string | null;
    errorMessage: string;
    formattedValue: string | null;
    fractionDigits: number;
    groupIndex: number;
    key: string;
    rawValue: number | null;
    title: string | null;
    unit: string | null;
    urn: string;
    value: number | null;
}

export type Api2ValueDataValue<T> = T | T[];

export interface Api2ValueDataCommonProps {
    calc?: MaybeArray<TimeSeriesCalc | undefined>;
    conversion?: MaybeNestedArray<number>;
    fractionDigits?: MaybeNestedArray<number>;
    offset?: MaybeNestedArray<number>;
    showDate?: boolean;
    title?: MaybeNestedArray<string | null | undefined>;
    unit?: MaybeNestedArray<string | null | undefined>;
    urn: string | string[] | string[][];
}

export function normalizeCalcValue(
    calc: MaybeArray<TimeSeriesCalc | undefined> | undefined,
    groupIndex: number,
): TimeSeriesCalc | undefined {
    if (Array.isArray(calc)) {
        return calc[groupIndex];
    }

    return calc;
}

function isNestedArray<T>(value: MaybeNestedArray<T>): value is T[][] {
    return Array.isArray(value) && Array.isArray(value[0]);
}

function normalizePropValue<T>(
    value: MaybeNestedArray<T> | undefined,
    groupIndex: number,
    itemIndex: number,
    useItemIndexForFlatArray: boolean,
    fallback: T,
): T {
    if (Array.isArray(value)) {
        if (isNestedArray(value)) {
            return value[groupIndex]?.[itemIndex] ?? fallback;
        }

        return value[useItemIndexForFlatArray ? itemIndex : groupIndex] ?? fallback;
    }

    return value ?? fallback;
}

function normalizeOptionalPropValue<T>(
    value: MaybeNestedArray<T | null | undefined> | undefined,
    groupIndex: number,
    itemIndex: number,
    useItemIndexForFlatArray: boolean,
): T | null {
    if (Array.isArray(value)) {
        if (isNestedArray(value)) {
            return value[groupIndex]?.[itemIndex] ?? null;
        }

        return value[useItemIndexForFlatArray ? itemIndex : groupIndex] ?? null;
    }

    return value ?? null;
}

export function normalizeUrnGroups(urn: string | string[] | string[][]): string[][] {
    if (Array.isArray(urn)) {
        if (Array.isArray(urn[0])) {
            return urn as string[][];
        }

        return (urn as string[]).map((currentUrn) => [currentUrn]);
    }

    return [[urn]];
}

export function createResolvedConfigs(
    aggregation: MaybeNestedArray<TimeSeriesAggregation>,
    conversion: MaybeNestedArray<number> | undefined,
    fractionDigits: MaybeNestedArray<number> | undefined,
    offset: MaybeNestedArray<number> | undefined,
    series: MaybeNestedArray<string | undefined> | undefined,
    title: MaybeNestedArray<string | null | undefined> | undefined,
    unit: MaybeNestedArray<string | null | undefined> | undefined,
    urn: string | string[] | string[][],
): Api2ValueDataResolvedConfig[] {
    const urnGroups = normalizeUrnGroups(urn);
    const useItemIndexForFlatArray = urnGroups.some((urnGroup) => urnGroup.length > 1);

    return urnGroups.flatMap((urnGroup, groupIndex) =>
        urnGroup.map((currentUrn, itemIndex) => ({
            aggregation: normalizePropValue(aggregation, groupIndex, itemIndex, useItemIndexForFlatArray, 'last'),
            conversion: normalizePropValue(conversion, groupIndex, itemIndex, useItemIndexForFlatArray, 1),
            fractionDigits: normalizePropValue(fractionDigits, groupIndex, itemIndex, useItemIndexForFlatArray, 2),
            groupIndex,
            key: `${currentUrn}-${groupIndex}-${itemIndex}`,
            offset: normalizePropValue(offset, groupIndex, itemIndex, useItemIndexForFlatArray, 0),
            series: normalizeOptionalPropValue(series, groupIndex, itemIndex, useItemIndexForFlatArray) ?? undefined,
            title: normalizeOptionalPropValue(title, groupIndex, itemIndex, useItemIndexForFlatArray),
            unit: normalizeOptionalPropValue(unit, groupIndex, itemIndex, useItemIndexForFlatArray),
            urn: currentUrn,
        })),
    );
}

export function createErrorItems(
    configs: Api2ValueDataResolvedConfig[],
    errorMessage: string,
): Api2ValueDataItem[] {
    return collapseItems(
        configs.map((config) => ({
            aggregation: config.aggregation,
            date: null,
            errorMessage,
            formattedValue: null,
            fractionDigits: config.fractionDigits,
            groupIndex: config.groupIndex,
            key: config.key,
            rawValue: null,
            title: config.title,
            unit: config.unit,
            urn: config.urn,
            value: null,
        })),
    );
}

function createBoxValues<T>(entries: BoxValueEntry<T>[]): BoxValues<T> {
    return new BoxValues(entries);
}

function createGroupAliasMap(groupItems: Api2ValueDataFlatItem[]): Map<string, string> {
    const aliasCounts = new Map<string, number>();

    groupItems.forEach((item) => {
        const alias = new ReverseDeviceSensor(item.urn).getSensorType(true);

        if (!alias || alias === item.urn) {
            return;
        }

        aliasCounts.set(alias, (aliasCounts.get(alias) ?? 0) + 1);
    });

    return groupItems.reduce<Map<string, string>>((result, item) => {
        const alias = new ReverseDeviceSensor(item.urn).getSensorType(true);

        if (!alias || alias === item.urn || aliasCounts.get(alias) !== 1) {
            return result;
        }

        result.set(item.urn, alias);
        return result;
    }, new Map<string, string>());
}

function createAliasedBoxValues<T>(
    groupItems: Api2ValueDataFlatItem[],
    getValue: (item: Api2ValueDataFlatItem) => T,
): BoxValues<T> {
    const aliasMap = createGroupAliasMap(groupItems);

    return createBoxValues(
        groupItems.map((item) => ({
            aliases: aliasMap.has(item.urn) ? [aliasMap.get(item.urn)!] : undefined,
            key: item.urn,
            value: getValue(item),
        })),
    );
}

export function collapseItems(flatItems: Api2ValueDataFlatItem[]): Api2ValueDataItem[] {
    const groupedItems = new Map<number, Api2ValueDataFlatItem[]>();

    flatItems.forEach((item) => {
        const currentGroup = groupedItems.get(item.groupIndex) ?? [];

        currentGroup.push(item);
        groupedItems.set(item.groupIndex, currentGroup);
    });

    return Array.from(groupedItems.entries())
        .sort(([leftGroupIndex], [rightGroupIndex]) => leftGroupIndex - rightGroupIndex)
        .map(([, groupItems]) => {
            if (groupItems.length === 1) {
                const [item] = groupItems;

                return {
                    aggregation: createAliasedBoxValues(groupItems, (currentItem) => currentItem.aggregation),
                    date: item.date,
                    errorMessage: item.errorMessage,
                    formattedValue: createAliasedBoxValues(groupItems, (currentItem) => currentItem.formattedValue),
                    fractionDigits: createAliasedBoxValues(groupItems, (currentItem) => currentItem.fractionDigits),
                    key: item.key,
                    rawValue: createAliasedBoxValues(groupItems, (currentItem) => currentItem.rawValue),
                    title: item.title,
                    unit: createAliasedBoxValues(groupItems, (currentItem) => currentItem.unit),
                    urn: createAliasedBoxValues(groupItems, (currentItem) => currentItem.urn),
                    value: item.value,
                };
            }

            const firstItem = groupItems[0];

            return {
                aggregation: createAliasedBoxValues(groupItems, (item) => item.aggregation),
                date: groupItems.map((item) => item.date),
                errorMessage: groupItems.map((item) => item.errorMessage),
                formattedValue: createAliasedBoxValues(groupItems, (item) => item.formattedValue),
                fractionDigits: createAliasedBoxValues(groupItems, (item) => item.fractionDigits),
                key: `group-${firstItem.groupIndex}`,
                rawValue: createAliasedBoxValues(groupItems, (item) => item.rawValue),
                title: firstItem?.title ?? null,
                unit: createAliasedBoxValues(groupItems, (item) => item.unit),
                urn: createAliasedBoxValues(groupItems, (item) => item.urn),
                value: groupItems.map((item) => item.value),
            };
        });
}
